import React, { Fragment, useEffect, useState }  from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';
import SeccionPanel from '../../Components/SeccionPanel';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';

import configuracionHelper from '../../Helpers/Configuracion';

const CAMPOS = ['index_charla_activa', 'index_charla_titulo', 'index_charla_mensaje', 'index_charla_boton'];

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

// Solo los campos del aviso: el PATCH de configuración no debe llevar calendario_pdf ni timestamps
const extraerAviso = configuracion => Object.fromEntries(CAMPOS.map(campo => [campo, configuracion?.[campo] ?? (campo === 'index_charla_activa' ? false : '')]));

const Switch = ({ id, etiqueta, checked, onChange }) => (
    <div className="switch" style={{margin:"10px 0 20px"}}>
        <p style={{margin:"0 0 5px"}}>{etiqueta}</p>
        <label>
            No
            <input id={id} type="checkbox" checked={Boolean(checked)} onChange={e => onChange(e.target.checked)} />
            <span className="lever"></span>
            Sí
        </label>
    </div>
);

const AvisoInicio = () => {
    const { recargar } = useAuth();

    const [guardado, setGuardado] = useState(null);
    const [aviso, setAviso] = useState(extraerAviso(null));
    const [subiendo, setSubiendo] = useState(false);

    useEffect(() => {
        obtenerAviso();
    }, []);

    const obtenerAviso = async () => {
        const res = await configuracionHelper.obtenerConfiguracion();
        if (res.success) {
            const datos = extraerAviso(res.data);
            setGuardado(datos);
            setAviso(datos);
        } else {
            Swal.fire('Error!', 'No se pudo obtener el aviso, codigo: ' + res.code, 'error');
        }
    }

    const hayCambios = Boolean(guardado) && JSON.stringify(aviso) !== JSON.stringify(guardado);

    const panel = usePanelEdicion({ hayCambios, onCerrar: () => guardado && setAviso(guardado) });

    // seccion=aviso: en preview el sitio pinta solo la ventana del aviso
    const { iframeRef, src } = usePreviewSitio({
        page: 'aviso-inicio',
        ruta: '/?seccion=aviso',
        data: guardado ? aviso : null
    });

    const cambiar = cambios => setAviso(aviso => ({ ...aviso, ...cambios }));

    const descartar = async () => {
        if (await confirmarDescartar()) setAviso(guardado);
    }

    const guardar = async e => {
        e.preventDefault();
        if (aviso.index_charla_activa) {
            const faltantes = [
                [aviso.index_charla_titulo, 'Título'],
                [aviso.index_charla_mensaje, 'Mensaje'],
                [aviso.index_charla_boton, 'Texto del botón']
            ].filter(([valor]) => !valor?.trim()).map(([, nombre]) => nombre);
            if (faltantes.length) return Swal.fire('Error!', 'Faltan datos por llenar: ' + faltantes.join(', '), 'error');
        }
        setSubiendo(true);
        const res = await configuracionHelper.editarConfiguracion(aviso);
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const input = (campo, etiqueta, ayuda) => (
        <Fragment>
            <div className="input-field">
                <input onChange={e => cambiar({ [campo]: e.target.value })} value={aviso[campo] ?? ''} id={campo} type="text" className="validate" maxLength={200} />
                <label className='active' htmlFor={campo}>{etiqueta}</label>
            </div>
            {ayuda && <p style={estiloAyuda}>{ayuda}</p>}
        </Fragment>
    );

    return (
        <Fragment>
            <SideNav />
            {subiendo && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo='Editar aviso'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambios}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={descartar} disabled={!hayCambios}>
                                Descartar
                            </button>
                            <button type='submit' form='form_aviso_inicio' className='boton_admin' disabled={!hayCambios}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {panel.abierto === 'editar' && guardado && (
                        <form id='form_aviso_inicio' onSubmit={guardar}>
                            <SeccionPanel numero={1} titulo='Aviso de charlas informativas' ubicacion='Ventana que aparece al entrar a la página de inicio.' activa>
                                <Switch
                                    id='index_charla_activa'
                                    etiqueta='Mostrar aviso al entrar a la página'
                                    checked={aviso.index_charla_activa}
                                    onChange={valor => cambiar({ index_charla_activa: valor })}
                                />
                                {input('index_charla_titulo', 'Título', 'Texto en negritas, arriba del mensaje')}
                                {input('index_charla_mensaje', 'Mensaje')}
                                {input('index_charla_boton', 'Texto del botón', 'El botón abre WhatsApp; el número no cambia')}
                            </SeccionPanel>
                        </form>
                    )}
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo='Vista previa del aviso de inicio'
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Aviso de inicio'
                    ayuda='La vista previa muestra la ventana tal como aparece al entrar a la página de inicio'
                    acciones={
                        <button type='button' className='boton_admin' onClick={() => panel.abrir('editar')}>
                            Editar aviso
                        </button>
                    }
                />
            </div>
        </Fragment>
    );
}

export default AvisoInicio;
